import React, { useEffect, useState } from "react";
import { getPrefixes, usersResource } from "../../helpers/configRoutes/backend_helper";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../../components/form/CustomInput";
import CustomSelect from "../../components/form/CustomSelect";
import { showFormErrors } from "../../helpers/showFormErrors";
import FileInput from "../../components/form/FileInput";
import CustomButton from "../../components/form/CustomButton";
import { validatePhones } from "../../helpers/forms/Validation";

const AddUpdOperator = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [prefixes, setPrefixes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Formik
    const validation = useFormik({
        initialValues: {
            id: null,
            active: true,
            email: "",
            first_name: "",
            last_name: "",
            role: "Operator",
            password: "",
            phone: "",
            prefix_id: "",
            company_id: "",
            profile_id: 3,
            imageProfile: "",
            payment_mile: "",
            driver_license: "",
            license_expiration: "",
            dot_physical_certificate: "",
            dot_physical_expiration: "",
            empty_mile_payment: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Invalid email").required("Email is required"),
            first_name: Yup.string().required("First name is required"),
            last_name: Yup.string().required("Last name is required"),
            phone: validatePhones(true),
            prefix_id: Yup.string().required("Prefix is required"),
            payment_mile: Yup.string().required("Payment per mile is required"),
            empty_mile_payment: Yup.string().required("Empty mile payment is required"),
            // Si quieres que el password no sea obligatorio al editar, se puede ajustar.
            password: Yup.string()
                .when("id", {
                    is: (val) => !val,
                    then: (schema) =>
                        schema
                            .required("Password is required")
                            .min(8, "Password must have at least 8 characters")
                            .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
                            .matches(/[a-z]/, "Password must contain at least one lowercase letter")
                            .matches(/[0-9]/, "Password must contain at least one number"),
                    otherwise: (schema) => schema.notRequired(),
                }),
            driver_license: Yup.string().required("Driver license is required"),
            license_expiration: Yup.date()
                .min(new Date(), "License expiration must be a future date")
                .required("License expiration is required"),
            dot_physical_certificate: Yup.string().required(
                "DOT Physical certificate is required"
            ),
            dot_physical_expiration: Yup.date()
                .min(new Date(), "DOT Physical expiration must be a future date")
                .required("DOT Physical expiration is required"),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            const response = await usersResource(
                values.id ? "put" : "post",
                values,
                values.id
            );

            if (response.status) {
                navigate("/operators"); // ajusta aquí si tu ruta es otra
            } else {
                setErrors(response.errors || {});
            }

            setLoading(false);
        },
    });

    // Cargar operador si es edición
    useEffect(() => {
        const getUser = async () => {
            const response = await usersResource("show", {}, id);
            if (response.status && response.user) {
                Object.keys(response.user).forEach((field) => {
                    validation.setFieldValue(field, response.user[field], false);
                });
            }
        };
        if (id) getUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Cargar catálogo de prefijos
    useEffect(() => {
        const getPrefixesCatalog = async () => {
            const response = await getPrefixes();
            if (response.status) {
                setPrefixes(response.prefixes || []);
            }
        };
        getPrefixesCatalog();
    }, []);

    return (
        <>
            <div className="min-h-screen bg-cs-surface">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold text-cs-dark">
                            {id ? "Update operator" : "Add new operator"}
                        </h1>
                        <p className="text-sm text-cs-muted mt-1">
                            {id
                                ? "Edit operator information, documentation and status."
                                : "Create a new operator, assign contact details and required documents."}
                        </p>
                    </div>
                </div>

                {/* Form */}
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        showFormErrors(validation);
                        return false;
                    }}
                >
                    <div
                        className="
                            max-w-auto mx-auto
                            bg-cs-bg
                            rounded-3xl
                            border border-cs-border
                            shadow-sm
                            px-6 py-6 md:px-8 md:py-8
                        "
                    >
                        {/* Avatar */}
                        <div className="flex justify-center mb-6">
                            <div className="flex flex-col items-center gap-2">
                                <img
                                    className="
                                        w-24 h-24 md:w-28 md:h-28
                                        rounded-full object-cover
                                        border-2 border-cs-primary-soft
                                    "
                                    src={
                                        validation.values.imageProfile ||
                                        "/images/usrAnonimo.png"
                                    }
                                    alt="Profile"
                                />
                                <FileInput
                                    format="img"
                                    name="imageProfile"
                                    validation={validation}
                                />
                                <p className="text-[10px] text-cs-muted">
                                    Recommended: square image, clear face, max 2MB.
                                </p>
                            </div>
                        </div>

                        {/* Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CustomInput
                                label="First name"
                                type="text"
                                name="first_name"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.first_name}
                                value={validation.values.first_name}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Last name"
                                type="text"
                                name="last_name"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.last_name}
                                value={validation.values.last_name}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Email"
                                type="email"
                                name="email"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.email}
                                value={validation.values.email}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label={id ? "Password (optional)" : "Password"}
                                type="password"
                                name="password"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.password}
                                value={validation.values.password}
                                handleChange={validation.handleChange}
                            />

                            <CustomSelect
                                label="Phone prefix"
                                options={prefixes}
                                name="prefix_id"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.prefix_id}
                                value={validation.values.prefix_id}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Phone number"
                                type="text"
                                name="phone"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.phone}
                                value={validation.values.phone}
                                handleChange={(e) => {
                                    const sanitized = e.target.value.replace(/\D/g, "");
                                    validation.setFieldValue("phone", sanitized);
                                }}
                            />

                            <CustomSelect
                                label="Status"
                                options={[
                                    { id: 1, name: "Active" },
                                    { id: 0, name: "Suspended" },
                                ]}
                                name="active"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={validation.errors.active}
                                value={validation.values.active}
                                handleChange={validation.handleChange}
                                disabled={
                                    validation.values.id
                                        ? !validation.values?.can?.disable
                                        : false
                                }
                            />
                            <div className=" grid grid-cols-2 md:grid-cols-2 gap-4">

                                <CustomInput
                                    label="Rate per mile"
                                    type="text"
                                    name="payment_mile"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={validation.errors.payment_mile}
                                    value={validation.values.payment_mile}
                                    handleChange={validation.handleChange}
                                />

                                <CustomInput
                                    label="Rate per empty mile"
                                    type="text"
                                    name="empty_mile_payment"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={validation.errors.empty_mile_payment}
                                    value={validation.values.empty_mile_payment}
                                    handleChange={validation.handleChange}
                                />

                            </div>
                        </div>

                        {/* License & DOT */}
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <CustomInput
                                    label="Driver license expiration"
                                    type="date"
                                    name="license_expiration"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={validation.errors.license_expiration}
                                    value={validation.values.license_expiration}
                                    handleChange={validation.handleChange}
                                />
                                <FileInput
                                    validation={validation}
                                    format="pdf"
                                    name="driver_license"
                                    nameInput="Driver license PDF"
                                    className="text-xs"
                                />
                            </div>

                            <div className="space-y-2">
                                <CustomInput
                                    label="DOT Physical expiration"
                                    type="date"
                                    name="dot_physical_expiration"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={validation.errors.dot_physical_expiration}
                                    value={validation.values.dot_physical_expiration}
                                    handleChange={validation.handleChange}
                                />
                                <FileInput
                                    validation={validation}
                                    format="pdf"
                                    name="dot_physical_certificate"
                                    nameInput="DOT Physical PDF"
                                    className="text-xs"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-8 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => navigate("/operators")}
                                className="
                                    px-4 py-2.5 rounded-xl
                                    text-xs md:text-sm
                                    border border-cs-border
                                    text-cs-muted
                                    hover:bg-cs-bg-alt
                                    transition-colors
                                "
                            >
                                Cancel
                            </button>

                            <CustomButton
                                loading={loading}
                                textLoad="Processing..."
                                type="submit"
                                className="
                                    px-6 py-2.5 rounded-xl
                                    bg-cs-primary hover:bg-cs-primary-hover
                                    text-white text-xs md:text-sm font-medium
                                    shadow-md hover:shadow-lg
                                    transition-all
                                "
                            >
                                {id ? "Save changes" : "Create operator"}
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AddUpdOperator;
